/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import authMgr from '@ion/applications/src/authentication/AuthenticationManager';
import {get} from '@ion/core/src/environment/environment';
import {info} from '@ion/core/src/logger/logger';
import {createListenerRegistry} from '@ion/core/src/utils/ListenerRegistry';
import EntityObserver from '@ion/services/src/websocket/EntityObserver';
import {SubscriptionResults} from '@ion/services/src/websocket/types';

export type SubscriptionResultListener = (results: SubscriptionResults) => void;
export type ConnectionListener = (connected: boolean) => void;

const reconnectionListeners = createListenerRegistry();
const subscriptionListeners = createListenerRegistry<[SubscriptionResults]>();
const connectionListeners   = createListenerRegistry<[boolean]>();

/**
 * Holds the connection state last handed to the connection listeners, such that
 * a listener is invoked on a *change* only. Initially `undefined` rather than
 * `false`, so the first event of a session is always delivered, whichever way
 * it goes.
 */
let lastNotifiedConnection: boolean | undefined;

/**
 * Notifies every registered connection listener, but only when the state
 * differs from the one last delivered.
 *
 * @param connected `true` once the socket is open, `false` once it has closed.
 */
const notifyConnectionChange = (connected: boolean): void => {
  if (lastNotifiedConnection !== connected) {
    lastNotifiedConnection = connected;
    connectionListeners.notify(connected);
  }
};

/**
 * Notifies every registered connection listener that the socket is open.
 * Passed as the `onConnect` callback to `EntityObserver.initialize`.
 *
 * Note, this fires for the *initial* connection only. A reconnection replaces
 * the underlying socket while its listeners are detached
 * (`Socket.reconnect` → `_initializeListeners(true)`), so the reopened socket's
 * `open` event reaches nothing; `notifyReconnected` is what reports it.
 */
const notifyConnected = (): void => {
  notifyConnectionChange(true);
};

/**
 * Notifies every registered connection listener that the socket has closed.
 * Passed as the `onDisconnect` callback to `EntityObserver.initialize`.
 *
 * Note, `onConnectionError` is deliberately left unwired: the socket always
 * follows an error with a `close`, so the disconnect is already reported here.
 */
const notifyDisconnected = (): void => {
  notifyConnectionChange(false);
};

/**
 * Notifies every registered reconnection listener. Passed as the
 * `onReconnection` callback to `EntityObserver.initialize`.
 */
const notifyReconnected = (attempt: number): void => {
  info(`[ADM] WebSocket reconnected after ${attempt} attempt(s) ...`);
  notifyConnectionChange(true);
  reconnectionListeners.notify();
};

/**
 * Notifies every registered subscription listener. Passed as the
 * `onSubscription` callback to `EntityObserver.initialize`.
 */
const notifySubscription = (results: SubscriptionResults): void => {
  subscriptionListeners.notify(results);
};

/**
 * Registers a listener to be invoked each time the WebSocket connection is
 * re-established after a disconnect, and returns a function which removes
 * that listener when invoked. `EntityObserver`'s own `onReconnection` is a
 * single config-level callback and `onInitialized` does not queue a callback
 * once the socket is already connected, so this registry is what lets a
 * consumer mounted mid-session still be told about a reconnect.
 */
export const onSocketReconnected = (listener: () => void): (() => void) => (
  reconnectionListeners.subscribe(listener)
);

/**
 * Registers a listener to be invoked with the results of every subscription
 * message the service answers - both subscribes and unsubscribes - and returns
 * a function which removes that listener when invoked.
 *
 * The results carry the entity and the succeeded/failed ids, but *not* which
 * action they answer, since the service does not send one. A caller which needs
 * to know whether its own subscribe has landed must therefore keep count of the
 * messages it sent for its own entity, rather than reading the reply.
 *
 * ```typescript
 *
 *   import {onSubscriptionResult} from '@/store/actions/websocket';
 *
 *   const dispose = onSubscriptionResult(({entity, succeeded}) => {
 *     // ... one reply for one message we sent ...
 *   });
 *
 * ```
 *
 * @param   listener Invoked with the results of each answered subscription.
 * @returns A function which removes the listener when invoked.
 */
export const onSubscriptionResult = (
  listener: SubscriptionResultListener
): (() => void) => (
  subscriptionListeners.subscribe(listener)
);

/**
 * Registers a listener to be invoked each time the WebSocket connection state
 * changes - `true` once the socket is open, `false` once it has closed - and
 * returns a function which removes that listener when invoked.
 *
 * Only changes are delivered, and only from the moment of registration; a
 * listener registered mid-session is not handed the current state. Read
 * `isSocketConnected()` for that.
 *
 * ```typescript
 *
 *   import {isSocketConnected, onSocketConnectionChange} from '@/store/actions/websocket';
 *
 *   setSocketConnected(isSocketConnected());
 *
 *   const dispose = onSocketConnectionChange(connected => {
 *     setSocketConnected(connected);
 *   });
 *
 * ```
 *
 * @param   listener Invoked with the new connection state on each change.
 * @returns A function which removes the listener when invoked.
 */
export const onSocketConnectionChange = (listener: ConnectionListener): (() => void) => (
  connectionListeners.subscribe(listener)
);

/**
 * Provides the current state of the WebSocket connection, for the initial read
 * of a consumer which registers before any connection event has fired.
 *
 * @returns `true` while the underlying socket is open, otherwise `false`.
 */
export const isSocketConnected = (): boolean => (
  EntityObserver.isConnected
);

/**
 * Initializes the ADM WebSocket connection via the `EntityObserver` framework,
 * authenticated with the current user's token (OIDC access token where OIDC is
 * enabled, the CTC session token otherwise). Intended to be called once per
 * sign-in, and not awaited by its caller: sign-in must not wait on a socket
 * handshake, and a socket that never connects must not fail sign-in.
 *
 * The `isInitialized` guard (`initialized && isConnected`) is false while a
 * connection is still pending; a second call then re-enters
 * `EntityObserver.initialize`, which is a no-op once initialized.
 *
 * No teardown is needed: every sign-out and session expiry ends in
 * `location.assign`, which discards all module state.
 */
export const initializeEntityObserver = async(): Promise<void> => {
  if (!EntityObserver.isInitialized) {
    await EntityObserver.initialize({
      authToken            : await authMgr.getToken(),
      appKey               : get('appKey'),
      url                  : get('websocketURL'),
      reconnectionInterval : get('websocketReconnectInterval'),
      onConnect            : notifyConnected,
      onDisconnect         : notifyDisconnected,
      onReconnection       : notifyReconnected,
      onSubscription       : notifySubscription
    });
  }
};
